import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FileText, AlertCircle, CheckCircle2 } from 'lucide-react';
import { draftEventFromMessage, type EventDraftResult } from '@/api/events';
import { getEventGraph, saveEventGraph } from '@/api/event-graph';

const fields = ['title', 'eventDate', 'city', 'state', 'foodInfo', 'imageUrl'] as const;
const fieldLabels = { title: 'Title', eventDate: 'Event date', city: 'City', state: 'State', foodInfo: 'Food guidance', imageUrl: 'Existing poster URL' };

export function EventPreparationPanel({ eventId }: { eventId?: string }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [edits, setEdits] = useState<Record<string, unknown>>({});
  const [result, setResult] = useState<EventDraftResult | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [revision, setRevision] = useState<number | null>(null);
  const [heldCodes, setHeldCodes] = useState<string[]>([]);
  const [reviewed, setReviewed] = useState(false);
  const [stale, setStale] = useState(false);

  const prepare = async () => {
    setBusy(true); setError(''); setResult(null); setSelected([]); setSaved(false); setReviewed(false); setStale(false);
    try {
      // Capture the saved selection before preparation. A later change must conflict.
      const graph = eventId ? (await getEventGraph(eventId)).data : null;
      const response = await draftEventFromMessage({
        text, today: new Date().toLocaleDateString('en-CA'),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        existingDraft: edits, editedFields: Object.keys(edits),
      });
      setResult(response.data);
      setRevision(graph?.revision ?? null);
      setHeldCodes(graph?.facets.map((f) => f.code) ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Preparation failed. Your edits remain below.');
    } finally { setBusy(false); }
  };
  const confirm = async () => {
    if (!eventId || revision === null || !result?.preparation.confirmationReceipt) return;
    setBusy(true); setError('');
    try {
      const response = await saveEventGraph(eventId, revision, [...new Set([...heldCodes, ...selected])], result.preparation.confirmationReceipt);
      qc.setQueryData(['event-graph', eventId], response);
      setSaved(true);
    } catch (e) { setError(e instanceof Error ? e.message : 'Save failed. Prepare again before retrying.'); }
    finally { setBusy(false); }
  };
  const changeText = (value: string) => {
    setText(value); setStale(true); setSelected([]); setReviewed(false); setSaved(false);
  };
  const preparation = result?.preparation;
  return (
    <section aria-label="Prepare event text" className="border border-gray-200 rounded-xl p-4 space-y-4 bg-white">
      <div>
        <h2 className="font-semibold flex items-center gap-2"><FileText size={18} aria-hidden="true" />Prepare from a message</h2>
        <p className="text-sm text-gray-600 mt-1">Paste your event text. Prepare runs only when you press the button. It does not create, publish, or change an event.</p>
      </div>
      <label className="block text-sm">Event message
        <textarea className="input mt-1 w-full" rows={5} maxLength={5000} value={text} disabled={busy} onChange={(e) => changeText(e.target.value)} />
      </label>
      <details>
        <summary className="text-sm cursor-pointer">Draft field edits</summary>
        <p className="text-xs text-gray-600 mt-2">These are preparation edits, not saved event details. Your edits, including cleared values and an existing poster, survive another Prepare.</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          {fields.map((field) => (
            <label className="text-sm" key={field}>{fieldLabels[field]}
              <input className="input mt-1" type={field === 'eventDate' ? 'date' : 'text'} disabled={busy}
                value={String(Object.prototype.hasOwnProperty.call(edits, field) ? edits[field] ?? '' : result?.draft[field] ?? '')}
                onChange={(e) => { setEdits((old) => ({ ...old, [field]: e.target.value || null })); setStale(true); setReviewed(false); setSelected([]); }} />
            </label>
          ))}
        </div>
      </details>
      <button className="btn-primary" disabled={busy || text.trim().length < 20} onClick={() => void prepare()}>{busy ? 'Working…' : 'Prepare'}</button>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {result && preparation && (
        <div className="space-y-3" aria-label="Preparation review">
          {stale && <p role="status" className="text-sm text-amber-800">The text or your edits changed. Prepare again before confirming attributes.</p>}
          {result.source === 'fallback' && <p role="status" className="text-sm text-amber-800">We could not read this message. Your edits are kept. Use the manual event form or try again.</p>}
          {preparation.clarifications.length > 0 && <div className="text-sm space-y-2">
            <h3 className="font-semibold flex items-center gap-2"><AlertCircle size={16} aria-hidden="true" />Check these details</h3>
            {preparation.clarifications.map((q) => <p key={q.field + q.reason}>{q.question}</p>)}
          </div>}
          {preparation.conflicts.map((conflict, i) => <div className="text-sm border border-amber-200 rounded-lg p-3" key={i}>
            <p className="font-semibold">{conflict.field} — needs review</p>
            {conflict.kind === 'existing_value' && <p>Your value: {conflict.heldValue ?? 'cleared'} · Message value: {conflict.suggestedValue ?? 'not stated'}</p>}
            {conflict.evidence.map((e) => <p key={e.start + ':' + e.end}>“{e.quote}”</p>)}
          </div>)}
          <div className="text-sm space-y-2">
            <h3 className="font-semibold">Suggested attributes — select only what you confirm</h3>
            {!preparation.suggestions.length && <p>No supported catalog attributes were found. You can select confirmed facts in the manual attribute editor.</p>}
            {preparation.suggestions.map((suggestion) => <label key={suggestion.code} className="flex items-start gap-2 border border-gray-200 rounded-lg p-3">
              <input type="checkbox" className="mt-1" disabled={busy || stale || saved} checked={selected.includes(suggestion.code)}
                onChange={(e) => setSelected((old) => e.target.checked ? [...old, suggestion.code] : old.filter((c) => c !== suggestion.code))} />
              <span><span className="block font-medium">{suggestion.label}</span>
                {suggestion.evidence.map((e) => <span className="block text-gray-600" key={e.start + ':' + e.end}>From your message: “{e.quote}”</span>)}
              </span>
            </label>)}
          </div>
          {preparation.fieldEvidence.length > 0 && <details className="text-sm">
            <summary className="cursor-pointer">Source excerpts for draft fields</summary>
            {preparation.fieldEvidence.map((f, i) => <p className="mt-2" key={i}>{f.field}: “{f.evidence.quote}”</p>)}
          </details>}
          <details className="text-xs"><summary className="cursor-pointer">Full draft preview</summary><pre className="overflow-auto mt-2 max-h-80">{JSON.stringify(result.draft, null, 2)}</pre></details>
          {eventId && preparation.suggestions.length > 0 && (
            <div className="space-y-3">
              <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" disabled={busy || stale || saved} checked={reviewed} onChange={(e) => setReviewed(e.target.checked)} />I reviewed this message and confirm the selected attributes for this event.</label>
              <button className="btn-primary" disabled={busy || stale || saved || !selected.length || !reviewed} onClick={() => void confirm()}>Confirm selected attributes</button>
              <p className="text-xs text-gray-600">Adds only your selected attributes. Existing confirmed attributes stay in place. Draft field edits are not saved by this action.</p>
            </div>
          )}
          {saved && <p role="status" className="text-sm text-green-700 flex items-center gap-2"><CheckCircle2 size={16} aria-hidden="true" />Selected attributes saved with organizer confirmation.</p>}
        </div>
      )}
    </section>
  );
}
